import React from "react";
import { Grid, GridColumn as Column, GridCellProps, GridRowProps } from "@progress/kendo-react-grid";
import { IExpansionProduct } from "src/interfaces/Reports/IExpansion";
import { formatCurrency, formatSignedCurrency, formatPercent } from "./format";

interface Props {
  rows: IExpansionProduct[]; // this month's products, already on the displayed basis
  factor: number; // 1 for MRR, 12 for ARR
  unitLabel: string; // "MRR" or "ARR"
  isLoading: boolean;
  error: string;
}

// The detail grid appends a total row. Starting/Ending/Net reconcile exactly to the
// month row above; gross Expansion and Contraction do not, because an account that
// grows one product while shrinking another nets out at account level but shows both
// movements here. That is the whole point of the breakdown, so the total is shown
// rather than hidden.
const TOTAL_PRODUCT = "All products";

const buildTotal = (rows: IExpansionProduct[]): IExpansionProduct => {
  const sum = (pick: (r: IExpansionProduct) => number) =>
    rows.reduce((acc, r) => acc + pick(r), 0);
  const startingMrr = sum((r) => r.startingMrr);
  const netExpansion = sum((r) => r.netExpansion);
  return {
    month: rows[0]?.month ?? "",
    product: TOTAL_PRODUCT,
    startingMrr,
    expansionAmount: sum((r) => r.expansionAmount),
    contractionAmount: sum((r) => r.contractionAmount),
    productDroppedMrr: sum((r) => r.productDroppedMrr),
    churnedMrr: sum((r) => r.churnedMrr),
    crossSellMrr: sum((r) => r.crossSellMrr),
    newMrr: sum((r) => r.newMrr),
    netExpansion,
    endingMrr: sum((r) => r.endingMrr),
    nrrPercent:
      startingMrr > 0
        ? Math.round(((startingMrr + netExpansion) / startingMrr) * 1000) / 10
        : 0,
    accountsExpanded: sum((r) => r.accountsExpanded),
    accountsContracted: sum((r) => r.accountsContracted),
    accountsDropped: sum((r) => r.accountsDropped),
    accountsChurned: sum((r) => r.accountsChurned),
  };
};

/**
 * Collapse every month into one row per product, for the grid's period-total row.
 * Movements are additive across months; Starting is the product's starting figure in
 * the first month and Ending its ending figure in the last, matching how the month
 * grid's own total row is built.
 */
export const buildPeriodProductRows = (rows: IExpansionProduct[]): IExpansionProduct[] => {
  if (rows.length === 0) return [];
  const months = [...new Set(rows.map((r) => r.month))].sort();
  const firstMonth = months[0];
  const lastMonth = months[months.length - 1];

  // Products in the order the API returned them for a single month.
  const products = rows.filter((r) => r.month === firstMonth).map((r) => r.product);

  return products.map((product) => {
    const forProduct = rows.filter((r) => r.product === product);
    const sum = (pick: (r: IExpansionProduct) => number) =>
      forProduct.reduce((acc, r) => acc + pick(r), 0);
    const startingMrr = forProduct.find((r) => r.month === firstMonth)?.startingMrr ?? 0;
    const netExpansion = sum((r) => r.netExpansion);
    return {
      month: "total",
      product,
      startingMrr,
      expansionAmount: sum((r) => r.expansionAmount),
      contractionAmount: sum((r) => r.contractionAmount),
      productDroppedMrr: sum((r) => r.productDroppedMrr),
      churnedMrr: sum((r) => r.churnedMrr),
      crossSellMrr: sum((r) => r.crossSellMrr),
      newMrr: sum((r) => r.newMrr),
      netExpansion,
      endingMrr: forProduct.find((r) => r.month === lastMonth)?.endingMrr ?? 0,
      nrrPercent:
        startingMrr > 0
          ? Math.round(((startingMrr + netExpansion) / startingMrr) * 1000) / 10
          : 0,
      accountsExpanded: sum((r) => r.accountsExpanded),
      accountsContracted: sum((r) => r.accountsContracted),
      accountsDropped: sum((r) => r.accountsDropped),
      accountsChurned: sum((r) => r.accountsChurned),
    };
  });
};

const ExpansionProductDetail: React.FC<Props> = ({ rows, factor, unitLabel, isLoading, error }) => {
  if (isLoading) {
    return (
      <div className="text-center p-3">
        <span className="spinner-border spinner-border-sm" role="status" />
        <span className="ms-2 text-muted">Loading product breakdown…</span>
      </div>
    );
  }
  if (error) return <div className="alert alert-danger m-2 mb-0">{error}</div>;
  if (rows.length === 0)
    return <p className="text-muted m-2 mb-0">No product detail for this month.</p>;

  const data = [...rows, buildTotal(rows)];

  const CurrencyCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!field) return <td />;
    return <td className="text-end">{formatCurrency((dataItem[field] ?? 0) * factor)}</td>;
  };

  // Gains: green when positive.
  const GainCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!field) return <td />;
    const value = dataItem[field] as number;
    return (
      <td className={`text-end ${value > 0 ? "text-success" : ""}`}>
        {formatCurrency(value * factor)}
      </td>
    );
  };

  // Losses: shown negative, red.
  const LossCell: React.FC<GridCellProps> = (props) => {
    const { dataItem, field } = props;
    if (!field) return <td />;
    const value = dataItem[field] as number;
    return (
      <td className={`text-end ${value > 0 ? "text-danger" : ""}`}>
        {value > 0 ? `-${formatCurrency(value * factor)}` : formatCurrency(0)}
      </td>
    );
  };

  const NetCell: React.FC<GridCellProps> = (props) => {
    const value = props.dataItem.netExpansion as number;
    const cls = value > 0 ? "text-success" : value < 0 ? "text-danger" : "";
    return (
      <td className={`text-end fw-semibold ${cls}`}>{formatSignedCurrency(value * factor)}</td>
    );
  };

  const NrrCell: React.FC<GridCellProps> = (props) => {
    const value = props.dataItem.nrrPercent as number;
    const cls = value >= 100 ? "text-success" : value > 0 ? "text-danger" : "text-muted";
    return (
      <td className={`text-end fw-semibold ${cls}`}>{value > 0 ? formatPercent(value) : "—"}</td>
    );
  };

  // "3 / 1 / 5" — accounts that shrank the product, dropped it, or cancelled outright.
  const MovedCell: React.FC<GridCellProps> = (props) => {
    const item = props.dataItem as IExpansionProduct;
    return (
      <td className="text-end text-muted">
        {item.accountsContracted} / {item.accountsDropped} / {item.accountsChurned}
      </td>
    );
  };

  const rowRender = (tr: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
    const item = props.dataItem as IExpansionProduct;
    if (item.product !== TOTAL_PRODUCT) return tr;
    const existing = (tr.props as any).className || "";
    return React.cloneElement(
      tr,
      { ...tr.props, className: `${existing} fw-bold border-top border-2` } as any,
      (tr.props as any).children,
    );
  };

  return (
    <div className="p-2">
      <Grid data={data} sortable={false} rowRender={rowRender}>
        <Column field="product" title="Product" width={190} />
        <Column field="startingMrr" title={`Starting ${unitLabel}`} cell={CurrencyCell} />
        <Column field="expansionAmount" title="Expansion" cell={GainCell} />
        <Column field="contractionAmount" title="Downgrade" cell={LossCell} />
        <Column field="productDroppedMrr" title="Dropped" cell={LossCell} />
        <Column field="churnedMrr" title="Cancelled" cell={LossCell} />
        <Column field="netExpansion" title="Net" cell={NetCell} />
        <Column field="crossSellMrr" title="Cross-sell" cell={GainCell} />
        <Column field="newMrr" title="New logo" cell={CurrencyCell} />
        <Column field="endingMrr" title={`Ending ${unitLabel}`} cell={CurrencyCell} />
        <Column field="nrrPercent" title="NRR" width={90} cell={NrrCell} />
        <Column title="Down/Drop/Cxl" width={130} cell={MovedCell} />
      </Grid>
      <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.75rem" }}>
        <strong>Downgrade</strong> = the account kept the product but spends less on it.{" "}
        <strong>Dropped</strong> = the product went to zero while the account stayed a
        customer. <strong>Cancelled</strong> = the whole account went to zero — a true
        account cancellation. The last column counts the accounts behind those three.
      </p>
    </div>
  );
};

export default ExpansionProductDetail;
